import { useCallback, useState } from "react";
import type { StravaConnectedServiceDto } from "../../../../common/model/connectedServiceDto";
import { useApi } from "../../contexts/apiContext";
import { ConnectStatus } from "../../domain/ConnectStatus";
import { ErrorInfo } from "../../domain/ErrorInfo";
import { getStravaAuthUrl } from "./stravaAuth";
import useStravaAuthCode from "./useStravaAuthCode";

/**
 * Result of applying the useUser custom hook.
 *
 * @property {UserSignupStatus} userSignupStatus - The current status of the user.
 * @property {() => void} startConnect - A function to start the user signup process by redirecting to Strava.
 * @property {() => void} finishConnectAsync - A function to finish the user signup process by exchanging the Strava auth code for an access token.
 */
export interface UseStravaResult {
  status: ConnectStatus;
  startConnect: () => void;
  finishConnectAsync: () => Promise<StravaConnectedServiceDto | null>;
  startSyncAsync: (continueFromHighWaterMark: boolean) => Promise<boolean>;
  stopSyncAsync: () => Promise<void>;
  error: ErrorInfo | null;
}

/**
 * This is a custom React hook that manages user data and related actions.
 *
 * @returns {UseStravaResult} An object containing user data, status, and related actions.
 */
export const useStrava = (): UseStravaResult => {
  const [status, setStatus] = useState<ConnectStatus>(ConnectStatus.NotChecked);
  const stravaAuthCode = useStravaAuthCode();
  const [error, setError] = useState<ErrorInfo | null>(null);
  const { connectStravaAsync, startStravaSyncAsync, stopStravaSyncAsync } = useApi();

  /**
   * Start the connect process by redirecting the user to the Strava authorization page.
   *
   * @function
   * @memberof UseStravaResult
   * @example
   * // Example usage of the startUserSignup function:
   * const { startConnect } = useStrava();
   * startConnect();
   */
  const startConnect = useCallback(() => {
    const canSignupUser = status === ConnectStatus.NotFound || status === ConnectStatus.NotChecked;

    if (canSignupUser) {
      setStatus(ConnectStatus.SigningUp);
      window.location.href = getStravaAuthUrl();
    }
  }, [status]);

  /**
   * Finish the connect process by exchanging the Strava auth code for an access token.
   * If the connection is successful, sets the user status to 'Ready'.
   * This function is memoized using the useCallback hook.
   *
   * @async
   * @function
   * @memberof UseStravaResult
   * @example
   * // Example usage of the finishUserSignup function:
   * const { finishConnect } = useStrava();
   * finishConnect();
   */
  const finishConnectAsync = useCallback(async (): Promise<StravaConnectedServiceDto | null> => {
    try {
      if (status !== ConnectStatus.Connecting && stravaAuthCode) {
        setStatus(ConnectStatus.Connecting);
        const strava = await connectStravaAsync(stravaAuthCode);
        setStatus(strava.accessToken ? ConnectStatus.Ready : ConnectStatus.NotFound);
        setError(null);
        return strava;
      }

      return null;
    } catch (error) {
      console.error("Error connecting to Strava: ", error);
      throw error;
    }
  }, [status, stravaAuthCode, connectStravaAsync]);

  /**
   * Start syncing activities with Strava.
   *
   * @async
   * @function
   * @memberof UseStravaResult
   * @example
   * // Example usage of the startSyncAsync function:
   * const { startSyncAsync } = useStrava();
   */
  const startSyncAsync = useCallback(
    async (_continueFromHighWaterMark: boolean): Promise<boolean> => {
      try {
        const result = await startStravaSyncAsync();
        setStatus(result.status === "IN_PROGRESS" ? ConnectStatus.Ready : status);
        setError(null);
        return result.status === "IN_PROGRESS";
      } catch (caughtError) {
        console.error("Error syncing activities with Strava: ", caughtError);
        setError(ErrorInfo.createNew("Error syncing activities with Strava", caughtError));
        return false;
      }
    },
    [startStravaSyncAsync, status],
  );

  /**
   * Stop syncing activities with Strava.
   *
   * @async
   * @function
   * @memberof UseStravaResult
   * @example
   * // Example usage of the stopSyncAsync function:
   * const { stopSyncAsync } = useStrava();
   */
  const stopSyncAsync = useCallback(async (): Promise<void> => {
    try {
      await stopStravaSyncAsync();
      setError(null);
    } catch (caughtError) {
      console.error("Error stopping sync activities with Strava: ", caughtError);
      setError(ErrorInfo.createNew("Error stopping sync activities with Strava", caughtError));
    }
  }, [stopStravaSyncAsync]);

  return { status, startConnect, finishConnectAsync, startSyncAsync, stopSyncAsync, error };
};
